import React, { useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types.ts';
import { useProcessingRuleById } from '@/modules/ProcessingDefinition/ProcessingRule/hooks';
import { useFieldsWithDefaultValues } from '@/utils';

import { useEnrollmentMatrixStore } from '../../ComplexConfiguration/EnrollmentMatrix/stores';
import { useHealthPlanFieldsState, useTemplateFieldState } from '../../hooks';
import CoverageCodeHint from '../CoverageCodeHint';
import { CUSTOM_TRIGGER_AMOUNT_PREFIX, HINT_RIGHT_ALIGN_KEY } from '../CoverageLevels.constants';
import { createTriggerAmountValidator } from '../CoverageLevels.utils';
import { useCoverageLevelsStore } from '../stores';

import useAccountFundingFields from './useAccountFundingFields';

interface FieldsConfig {
  isTemplateMode?: boolean;
  isEditMode?: boolean;
  isActiveMode?: boolean;
}

const useCoverageLevelsFields = (id: string, config?: FieldsConfig) => {
  const enrollmentMatrixState = useEnrollmentMatrixStore((state) => state.state);
  const { rule } = useProcessingRuleById(enrollmentMatrixState?.processingRuleName);

  const sourceState = useCoverageLevelsStore((state) =>
    state.sourceState?.find((item) => item && item.coverageId === id)
  );
  const currentState = useCoverageLevelsStore((state) =>
    state.state?.find((item) => item && item.coverageId === id)
  );

  const accountFundingFields = useAccountFundingFields(
    currentState?.accountFundingConfigs,
    currentState?.categorySpendLimits
  );

  // Collect unique triggers from all non-last tiers (ordered by tier)
  // Build validation: triggers of same category_type must increase across tiers
  const customTriggerFields: Field[] = useMemo(() => {
    if (!rule?.tiers?.length) return [];

    const tiers = rule.tiers;
    const addedFieldKeys = new Set<string>();
    const fields: Field[] = [];

    // Track trigger order by category type for validation
    // Key: trigger_category_type, Value: array of trigger keys in tier order
    const triggersByCategory: Record<string, { key: string; name: string }[]> = {};

    tiers.forEach((tier, index) => {
      const isLastOrOnly = index === tiers.length - 1 || tiers.length <= 1;
      if (isLastOrOnly) return;

      (tier.tierTriggers || []).forEach((trigger) => {
        const key = `${CUSTOM_TRIGGER_AMOUNT_PREFIX}${trigger.id}`;
        const categoryType = trigger.triggerCategoryType || '';

        if (!triggersByCategory[categoryType]) {
          triggersByCategory[categoryType] = [];
        }
        triggersByCategory[categoryType].push({ key, name: trigger.name || `Trigger ${trigger.id}` });
      });
    });

    // Build a map: for each tier, find the FAMILY trigger key (for embedded <= family validation)
    const familyTriggerKeyByTier: Record<number, { key: string; name: string }> = {};
    tiers.forEach((tier, index) => {
      const isLastOrOnly = index === tiers.length - 1 || tiers.length <= 1;
      if (isLastOrOnly) return;
      const familyTrigger = (tier.tierTriggers || []).find(
        (t) => t.triggerCategoryType === TriggerCategory.FAMILY
      );
      if (familyTrigger) {
        familyTriggerKeyByTier[index] = {
          key: `${CUSTOM_TRIGGER_AMOUNT_PREFIX}${familyTrigger.id}`,
          name: familyTrigger.name || `Trigger ${familyTrigger.id}`,
        };
      }
    });

    // Build fields with validation
    tiers.forEach((tier, index) => {
      const isLastOrOnly = index === tiers.length - 1 || tiers.length <= 1;
      if (isLastOrOnly) return;

      (tier.tierTriggers || []).forEach((trigger) => {
        const key = `${CUSTOM_TRIGGER_AMOUNT_PREFIX}${trigger.id}`;
        if (addedFieldKeys.has(key)) return;
        addedFieldKeys.add(key);

        const categoryType = trigger.triggerCategoryType || '';
        const sameCategoryTriggers = triggersByCategory[categoryType] || [];
        const currentIndex = sameCategoryTriggers.findIndex((t) => t.key === key);
        const previousTriggers = currentIndex > 0 ? sameCategoryTriggers.slice(0, currentIndex) : [];

        // Embedded triggers have additional constraint: must be <= Family trigger of same tier
        const maxTrigger =
          categoryType === TriggerCategory.EMBEDDED ? familyTriggerKeyByTier[index] : undefined;

        const validator = config?.isActiveMode
          ? createTriggerAmountValidator({ previousTriggers, maxTrigger })
          : VALIDATORS.STRING;

        fields.push({
          name: key,
          type: FieldTypes.Currency,
          label: trigger.name || `Trigger ${trigger.id}`,
          placeholder: '',
          showRequireIcon: true,
          validator,
        });
      });
    });

    return fields;
  }, [rule?.tiers, config?.isActiveMode]);

  const deductibleFields: Field[] = useMemo(() => {
    const coverageCodeField: Field = {
      name: 'coverageCode',
      type: FieldTypes.Text,
      label: 'Coverage code',
      placeholder: '',
      showRequireIcon: true,
      validator: VALIDATORS.REQUIRED_STRING,
      hintAlign: HINT_RIGHT_ALIGN_KEY,
      hint: <CoverageCodeHint />,
    };

    return [coverageCodeField, ...customTriggerFields];
  }, [customTriggerFields]);

  const fields: Field[] = useMemo(
    () => [...deductibleFields, ...accountFundingFields],
    [deductibleFields, accountFundingFields]
  );

  const filteredField = useHealthPlanFieldsState(fields, id);

  // Flatten customTriggerAmounts into sourceState for useFieldsWithDefaultValues
  const flatSourceState = useMemo(() => {
    if (!sourceState?.customTriggerAmounts) return sourceState;
    return { ...sourceState, ...sourceState.customTriggerAmounts };
  }, [sourceState]);

  const formattedFields = useFieldsWithDefaultValues(filteredField, flatSourceState);
  const stateFields = useTemplateFieldState(formattedFields, config?.isTemplateMode, config?.isEditMode, id);

  return stateFields;
};

export default useCoverageLevelsFields;
